import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import messages from "../../../../../assets/locale/messages";
import { showErrorMsg } from "../../../../../utils/Helpers";
import { UIActionModal } from "../../../../ui";
import { fillTemplate } from "../../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import BankTransferDetails from "../../../Form/Steps/PublishAndPay/BankTransferDetails";
import PaymentMethodField from "../../../Form/Steps/PublishAndPay/PaymentMethodField";
import TransferEvidence from "../../../Form/Steps/PublishAndPay/TransferEvidence";
import useBankAccountInfo from "../../../Form/Steps/PublishAndPay/useBankAccountInfo";
import useEvidenceUpload, {
  UPLOAD_STATUS,
} from "../../../Form/Steps/PublishAndPay/useEvidenceUpload";
import UpgradeApprovalNotice from "../UpgradeApprovalNotice";

import "../../../Form/Steps/PublishAndPay/PublishAndPay.scss";
import "../_CrewSizeUpgradeModals.scss";

/** Same payment step as publishing: bank transfer details and the transfer evidence. */
const UpgradePaymentModal = ({ open, newCrewSizeName, onClose, onSubmit }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared, clientReports } = messages[lang];
  const t = events.viewEvents.crewSizeUpgrade;
  const payT = events.creatEvent.publishPay;

  const upload = useEvidenceUpload({
    // The upload calls skip the interceptor's toast, so the error is shown here.
    onError: (error) =>
      showErrorMsg(
        error?.response ? shared.generalErrMsg : shared.noInternetConnection,
      ),
  });
  const bankDetails = useBankAccountInfo();

  return (
    <UIActionModal
      open={open}
      variant="info"
      className="crew-size-upgrade-modal"
      title={t.paymentModal.title}
      onClose={onClose}
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={payT.submitForApproval}
          confirmDisabled={upload.status !== UPLOAD_STATUS.uploaded}
          onCancel={onClose}
          onConfirm={() => onSubmit(upload.url)}
        />
      }
    >
      <UIActionModal.Divider />
      <div className="crew-size-upgrade__body">
        <Typography
          component="p"
          className="text-body-sm crew-size-upgrade__message"
        >
          {fillTemplate(t.paymentModal.description, {
            size: newCrewSizeName,
          })}
        </Typography>

        <section className="crew-size-upgrade__section">
          <Typography
            component="h3"
            className="text-label-md crew-size-upgrade__label"
          >
            {payT.paymentMethod}
          </Typography>
          <PaymentMethodField t={payT} stacked />
        </section>

        <BankTransferDetails
          t={payT}
          details={bankDetails}
          compact
        />
        <TransferEvidence
          t={payT}
          shared={shared}
          previewT={clientReports.previewModal}
          isRtl={lang === "ar"}
          upload={upload}
        />

        <UpgradeApprovalNotice t={t} />
      </div>
    </UIActionModal>
  );
};

UpgradePaymentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  newCrewSizeName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Called with the evidence link once it is uploaded and "Submit for Approval" is pressed. */
  onSubmit: PropTypes.func.isRequired,
};

export default UpgradePaymentModal;
